import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

type KieThrottleResponse = {
  scope: "per-process" | string;
  windowUsage: number;
  limit: number;
  windowMs: number;
  reserve: number;
  interactiveMaxWaitMs: number;
  queueDepth: number;
  queueDepthByLane: {
    interactive: number;
    batch: number;
    video: number;
  };
  topUsers: Array<{ userId: string; queued: number }>;
  oldestQueuedAgeMs: number;
};

const throttleKey = ["/api/admin/kie-throttle"];

function formatDuration(milliseconds: number) {
  if (milliseconds < 1000) return `${Math.max(0, Math.round(milliseconds))} ms`;
  const seconds = milliseconds / 1000;
  if (seconds < 60) return `${Number(seconds.toFixed(1))} sec`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return remainingSeconds > 0 ? `${minutes} min ${remainingSeconds} sec` : `${minutes} min`;
}

function Metric({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return (
    <div className="rounded-md border bg-muted/30 px-3 py-3">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p className="mt-1 font-mono text-lg font-semibold tabular-nums">{value}</p>
      {detail && <p className="mt-1 text-xs text-muted-foreground">{detail}</p>}
    </div>
  );
}

export function KieThrottleCard() {
  const query = useQuery<KieThrottleResponse>({
    queryKey: throttleKey,
    queryFn: async () => (await apiRequest("GET", throttleKey[0])).json(),
    refetchInterval: 15000,
  });

  return (
    <Card data-testid="kie-throttle-card">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle>KIE throttle</CardTitle>
          {query.data && (
            <Badge variant="outline" className="bg-muted/40 font-medium">
              Per process
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Limits and queue depth are reported for this server process only. They are not a cluster-wide total.
        </p>

        {query.isLoading ? (
          <div aria-label="Loading KIE throttle" className="space-y-3" data-testid="kie-throttle-loading">
            <div className="h-20 animate-pulse rounded-md bg-muted" />
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[0, 1, 2, 3].map((item) => <div key={item} className="h-20 animate-pulse rounded-md bg-muted" />)}
            </div>
          </div>
        ) : query.isError ? (
          <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm">
            <p>Could not load KIE throttle status.</p>
            <Button className="mt-3" variant="outline" onClick={() => void query.refetch()}>Retry</Button>
          </div>
        ) : query.data ? (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <Metric
                label="Window usage"
                value={`${query.data.windowUsage.toLocaleString()} / ${query.data.limit.toLocaleString()}`}
                detail={`Window ${formatDuration(query.data.windowMs)} · reserve ${query.data.reserve.toLocaleString()}`}
              />
              <Metric
                label="Queued requests"
                value={query.data.queueDepth.toLocaleString()}
                detail={`Oldest waiting ${formatDuration(query.data.oldestQueuedAgeMs)}`}
              />
            </div>

            <section aria-labelledby="kie-throttle-lanes">
              <h3 id="kie-throttle-lanes" className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Queue by lane
              </h3>
              <div className="grid grid-cols-3 gap-2">
                <Metric label="Interactive" value={query.data.queueDepthByLane.interactive.toLocaleString()} />
                <Metric label="Batch" value={query.data.queueDepthByLane.batch.toLocaleString()} />
                <Metric label="Video" value={query.data.queueDepthByLane.video.toLocaleString()} />
              </div>
            </section>

            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t pt-3 text-xs text-muted-foreground">
              <span>Interactive max wait: {formatDuration(query.data.interactiveMaxWaitMs)}</span>
              <span>Scope: {query.data.scope}</span>
            </div>

            <section aria-labelledby="kie-throttle-top-users">
              <h3 id="kie-throttle-top-users" className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Top queued users
              </h3>
              {query.data.topUsers.length === 0 ? (
                <div className="rounded-md border border-dashed bg-muted/20 px-4 py-5 text-center">
                  <p className="text-sm font-medium">No users are queued</p>
                  <p className="mt-1 text-xs text-muted-foreground">Queued user counts will appear here when requests are waiting.</p>
                </div>
              ) : (
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th scope="col" className="px-3 py-2 font-medium">User ID</th>
                        <th scope="col" className="px-3 py-2 text-right font-medium">Queued</th>
                      </tr>
                    </thead>
                    <tbody>
                      {query.data.topUsers.map((user) => (
                        <tr key={user.userId} className="border-t">
                          <th scope="row" className="max-w-[260px] truncate px-3 py-2 font-mono text-xs font-medium">
                            {user.userId}
                          </th>
                          <td className="px-3 py-2 text-right font-mono tabular-nums">{user.queued.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        ) : (
          <div className="rounded-md border border-dashed bg-muted/20 px-4 py-6 text-center" data-testid="kie-throttle-empty">
            <p className="text-sm font-medium">Throttle status is unavailable</p>
            <p className="mt-1 text-xs text-muted-foreground">No status data was returned.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
